import React, { useState, useEffect, useMemo } from 'react';
import { Paper, IconButton, Box, TextField, Autocomplete, InputAdornment, TableRow, TableCell, MenuItem, Tooltip, Typography } from '@mui/material'; // Thêm Tooltip, Typography
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from '../DataTable';
import * as API from '../../api';
import { formatCurrency } from '../../utils/currencyUtils';
import TransportRouteDialog from './TransportRouteDialog';

const VEHICLE_TYPE_CHOICES = [
    { value: 'SHUTTLE', label: 'Shuttle Bus' },
    { value: 'LIMO', label: 'Limousine' },
    { value: 'DCAR', label: 'Dcar' },
    { value: 'SINGLE CABIN', label: 'Cabin Đơn' },
    { value: 'COUPLE CABIN', label: 'Cabin Đôi' },
    { value: 'SINGLE SLEEPER', label: 'Giường Nằm Đơn' },
    { value: 'COUPLE SLEEPER', label: 'Giường Nằm Đôi' },
    { value: '4-SEAT TAXI', label: 'Taxi 4 Chỗ' },
    { value: '7-SEAT TAXI', label: 'Taxi 7 chỗ' }
];

const TransportRouteTab = ({ openAddSignal, onCloseAdd }) => {
    const [data, setData] = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    // Filter states
    const [searchTerm, setSearchTerm] = useState(''); // Tìm tên, mã
    const [searchPickup, setSearchPickup] = useState(''); // Tìm điểm đón
    const [searchDropoff, setSearchDropoff] = useState(''); // Tìm điểm trả
    const [filterSupplier, setFilterSupplier] = useState(null);
    const [filterVehicleType, setFilterVehicleType] = useState('ALL');

    const userRole = localStorage.getItem('user_role') || 'SALE';

    const loadData = async () => {
        try {
            const res = await API.fetchTransportRoutes();
            setData(res.data || (res.data?.results || []));
        } catch (e) { 
            console.error(e); 
        }
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        if (openAddSignal) {
            setEditItem(null);
            setIsOpen(true);
        }
    }, [openAddSignal]);

    const handleDelete = async (code) => {
        if (window.confirm(`Bạn có chắc chắn muốn xóa tuyến xe [${code}] không? Các điều xe cũ trong Booking đã lưu vẫn sẽ giữ nguyên giá trị.`)) {
            try {
                await API.deleteTransportRoute(code);
                loadData();
            } catch (e) { 
                alert('Không thể xóa tuyến xe này vì đã phát sinh dữ liệu liên quan.'); 
            }
        }
    };

    // Lấy danh sách NCC duy nhất từ data để làm filter
    const uniqueSuppliers = useMemo(() => {
        const names = data.map(t => t.supplier_name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [data]);

    // Lọc data theo bộ lọc
    const filteredData = useMemo(() => {
        return data.filter(item => {
            // 1. Lọc theo Tên hoặc Mã tuyến
            if (searchTerm && 
                !item.route_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !item.route_code.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
                return false;
            }
            
            // 2. Lọc theo Điểm đón (Tìm trong mảng JSON)
            if (searchPickup) {
                const pickupStr = JSON.stringify(item.pickup_point || []).toLowerCase();
                if (!pickupStr.includes(searchPickup.toLowerCase())) return false;
            }

            // 3. Lọc theo Điểm trả (Tìm trong mảng JSON)
            if (searchDropoff) {
                const dropoffStr = JSON.stringify(item.dropoff_point || []).toLowerCase();
                if (!dropoffStr.includes(searchDropoff.toLowerCase())) return false;
            }
            
            if (filterSupplier && item.supplier_name !== filterSupplier) return false;
            if (filterVehicleType !== 'ALL' && item.vehicle_type !== filterVehicleType) return false;

            return true;
        });
    }, [data, searchTerm, searchPickup, searchDropoff, filterSupplier, filterVehicleType]);

    const columns = [
        { label: 'Mã Tuyến', field: 'route_code', minWidth: 90 },
        { label: 'Tên Tuyến', field: 'route_name', minWidth: 150 },
        { label: 'Nhà Xe (NCC)', field: 'supplier_name', minWidth: 120 },
        { label: 'Loại xe', field: 'vehicle_type_display', minWidth: 100 },
        { label: 'Các điểm đón', field: 'pickup_point', minWidth: 260 },
        { label: 'Các điểm trả', field: 'dropoff_point', minWidth: 260 },
        { label: 'Giá Net', field: 'cost', minWidth: 100, align: 'right' },
        { label: 'Giá Bán', field: 'price', minWidth: 100, align: 'right' },
        { label: '', field: 'action', minWidth: 80, align: 'center' }
    ];

    // Hàm helper để render danh sách điểm đón trả từ mảng JSON
    const renderPoints = (points) => {
        if (!points || !Array.isArray(points) || points.length === 0) return '---';
        return points.map((p, index) => (
            <Box key={index} sx={{ mb: 0.5, pb: 0.5, borderBottom: index < points.length - 1 ? '1px dashed #eee' : 'none' }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {p.time && (
                        <span style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px', marginRight: '6px', fontWeight: 'bold', color: '#555' }}>
                            {p.time}
                        </span>
                    )}
                    {p.province && <strong>[{p.province}] </strong>}
                    {p.address}
                </Typography>
            </Box>
        ));
    };

    return (
        <Paper sx={{ p: 2 }}>
            {/* Vùng bộ lọc */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Tìm tên, mã tuyến..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    }}
                />

                <TextField
                    size="small"
                    placeholder="Lọc điểm đón..."
                    value={searchPickup}
                    onChange={(e) => setSearchPickup(e.target.value)}
                    sx={{ width: 180 }}
                />

                <TextField
                    size="small"
                    placeholder="Lọc điểm trả..."
                    value={searchDropoff}
                    onChange={(e) => setSearchDropoff(e.target.value)}
                    sx={{ width: 180 }}
                />
                
                <Autocomplete
                    size="small"
                    options={uniqueSuppliers}
                    value={filterSupplier}
                    onChange={(e, val) => setFilterSupplier(val)}
                    renderInput={(params) => <TextField {...params} label="Nhà cung cấp (Nhà xe)" />}
                    sx={{ width: 220 }}
                />

                <TextField
                    select
                    size="small"
                    label="Loại xe"
                    value={filterVehicleType}
                    onChange={(e) => setFilterVehicleType(e.target.value)}
                    sx={{ width: 180 }}
                >
                    <MenuItem value="ALL">Tất cả xe</MenuItem>
                    {VEHICLE_TYPE_CHOICES.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            </Box>

            {/* Bảng dữ liệu */}
            <DataTable
                columns={columns}
                data={filteredData}
                renderRow={(row) => (
                    <TableRow key={row.route_code} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row.route_code}</TableCell>
                        <TableCell>{row.route_name}</TableCell>
                        <TableCell>
                            {/* TOOLTIP HIỂN THỊ THÔNG TIN LIÊN HỆ */}
                            <Tooltip 
                                title={
                                    <React.Fragment>
                                        <Typography variant="body2">SĐT: {row.supplier_phone || 'Chưa cập nhật'}</Typography>
                                        <Typography variant="body2">Email: {row.supplier_email || 'Chưa cập nhật'}</Typography>
                                    </React.Fragment>
                                } 
                                arrow 
                                placement="top"
                            >
                                <span style={{ cursor: 'pointer', borderBottom: '1px dotted #888' }}>
                                    {row.supplier_name}
                                </span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#ed6c02' }}>{row.vehicle_type_display}</TableCell>
                        <TableCell sx={{ color: '#333' }}>
                            {renderPoints(row.pickup_point)}
                        </TableCell>
                        <TableCell sx={{ color: '#333' }}>
                            {renderPoints(row.dropoff_point)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#d32f2f' }}>
                            {formatCurrency(row.cost_price)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            {formatCurrency(row.selling_price)}
                        </TableCell>
                        <TableCell align="center">
                        {['MANAGER', 'SOURCING'].includes(userRole) ? (
                            <>
                            <IconButton size="small" color="primary" onClick={() => { setEditItem(row); setIsOpen(true); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.route_code)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                        ) : (
                            <Typography variant="caption" color="text.secondary">Chỉ xem</Typography>
                        )}
                        </TableCell>
                    </TableRow>
                )}
            />
            
            {/* Modal Form Thêm/Sửa */}
            <TransportRouteDialog
                open={isOpen}
                onClose={() => { setIsOpen(false); onCloseAdd(); }}
                initialData={editItem}
                onSuccess={() => { setIsOpen(false); onCloseAdd(); loadData(); }}
            />
        </Paper>
    );
};

export default TransportRouteTab;